import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import React, { useRef } from 'react';
import Barcode from 'react-barcode';
import {
  MdCheckCircle,
  MdDownload,
  MdEvent,
  MdLocationOn,
  MdPhotoCamera,
  MdPrint,
} from 'react-icons/md';
import main_logo from '../../assets/logos/main_logo.png';

import { PartyData } from '@types';
import { excludeFromTicketCapture, SuccessAppDownloadBanner } from '_atoms';
import HelperMethods from '_utils/HelperMethods';

interface PartyConfirmationProps {
  partyData: PartyData;
  partyNumber: string;
}

export const PartyConfirmation: React.FC<PartyConfirmationProps> = ({
  partyData,
  partyNumber,
}) => {
  const ticketElementRef = useRef<HTMLDivElement>(null);

  const calculateTotal = () => {
    const packagePrice = partyData.selectedPartyPackage?.price || 0;
    const extrasPrice = partyData.selectedExtras.reduce(
      (total, extraSelection) =>
        total + extraSelection.extra.price * extraSelection.quantity,
      0,
    );
    const pizzasPrice = partyData.selectedPizzas.reduce(
      (total, pizzaSelection) =>
        total + pizzaSelection.pizza.price * pizzaSelection.quantity,
      0,
    );
    return packagePrice + extrasPrice + pizzasPrice;
  };

  const calculateConvenienceFee = () => {
    const subtotal = calculateTotal();
    return subtotal * 0.03;
  };

  const calculateFinalTotal = () => {
    const subtotal = calculateTotal();
    const convenienceFee = calculateConvenienceFee();
    return subtotal + convenienceFee;
  };

  const generateTicketPDF = async (): Promise<{
    pdf: jsPDF;
    filename: string;
  }> => {
    if (!ticketElementRef.current) {
      throw new Error('Ticket element not found');
    }

    // Convert the ticket element to an image
    const png = await toPng(ticketElementRef.current, {
      quality: 1.0,
      backgroundColor: '#ffffff',
      filter: excludeFromTicketCapture,
    });

    // Create a new jsPDF instance
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // Create an image element to get dimensions
    const img = new Image();
    img.src = png;

    return new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          // Calculate dimensions to fit the image on the page
          const imgWidth = pageWidth - margin * 2;
          const imgHeight = (img.height * imgWidth) / img.width;

          // If image is too tall, scale it down
          let finalWidth = imgWidth;
          let finalHeight = imgHeight;

          if (imgHeight > pageHeight - margin * 2) {
            finalHeight = pageHeight - margin * 2;
            finalWidth = (img.width * finalHeight) / img.height;
          }

          // Center the image on the page
          const x = (pageWidth - finalWidth) / 2;
          const y = (pageHeight - finalHeight) / 2;

          // Add the image to the PDF
          pdf.addImage(png, 'PNG', x, y, finalWidth, finalHeight);

          // Generate filename using party package name
          const filename = `MOI_Party_${partyData.selectedPartyPackage?.name.replace(/ /g, '_') || 'Booking'}.pdf`;

          resolve({ pdf, filename });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load ticket image'));
      };
    });
  };

  const handleDownloadPDF = async () => {
    try {
      const { pdf, filename } = await generateTicketPDF();
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handlePrint = async () => {
    try {
      const { pdf } = await generateTicketPDF();
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl);
      if (printWindow) {
        printWindow.print();
      }
    } catch (error) {
      console.error('Error printing:', error);
    }
  };

  const handleSavePhoto = async () => {
    try {
      if (!ticketElementRef.current) {
        throw new Error('Ticket element not found');
      }

      // Convert the ticket element to a PNG image
      const dataUrl = await toPng(ticketElementRef.current, {
        quality: 1.0,
        backgroundColor: '#ffffff',
        pixelRatio: 2, // Higher resolution for better quality
        filter: excludeFromTicketCapture,
      });

      // Create a download link
      const partyDate = new Date(partyData.selectedTimeSlot?.date || '');
      const dateStr = partyDate.toISOString().split('T')[0]; // YYYY-MM-DD
      const link = document.createElement('a');
      link.download = `MOI_Party_${dateStr}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error saving photo:', error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Ticket Content */}
      <div ref={ticketElementRef} className="space-y-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={main_logo} alt="MOI Logo" className="h-12 w-auto" />
              <div>
                <h1 className="text-2xl font-bold">
                  Party Booking Confirmation
                </h1>
                <p className="text-blue-100">
                  Your party has been successfully booked!
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <MdCheckCircle className="text-green-400 w-6 h-6" />
                <span className="font-semibold">Confirmed</span>
              </div>
              <p className="text-sm text-blue-100">
                {HelperMethods.formatDateTimeLocal(new Date().toISOString())}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Party Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {partyData.selectedPartyPackage && (
                <div className="flex items-center space-x-3">
                  <MdEvent className="text-blue-600 w-5 h-5" />
                  <div>
                    <p className="text-sm text-gray-600">Party Package</p>
                    <p className="font-medium text-gray-900">
                      {partyData.selectedPartyPackage.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      ${partyData.selectedPartyPackage.price}
                    </p>
                  </div>
                </div>
              )}

              {partyData.selectedTimeSlot && (
                <div className="flex items-center space-x-3">
                  <MdEvent className="text-blue-600 w-5 h-5" />
                  <div>
                    <p className="text-sm text-gray-600">Date & Time</p>
                    <p className="font-medium text-gray-900">
                      {HelperMethods.formatDate(
                        partyData.selectedTimeSlot.date,
                      )}
                    </p>
                    <p className="font-medium text-gray-900">
                      {HelperMethods.formatTime12Hour(
                        partyData.selectedTimeSlot.timeSlot,
                      )}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <MdLocationOn className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <a
                    href="https://www.google.com/maps/dir/Modesto+on+Ice,+11th+St,+Modesto,+CA+95354"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
                  >
                    1131 11th Street • Modesto, CA 95354
                  </a>
                </div>
              </div>
            </div>

            {/* Barcode */}
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Barcode
                </p>
                <div className="bg-white border border-gray-300 p-3 rounded">
                  <div className="font-mono text-xs leading-tight text-center flex items-center justify-center">
                    <Barcode value={partyNumber} format="CODE128" />
                  </div>
                  <p className="text-xs text-center text-gray-600 mt-1">
                    {partyNumber}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Extras */}
          {partyData.selectedExtras.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">
                Selected Extras
              </h4>
              <div className="bg-gray-50 p-4 rounded space-y-2">
                {partyData.selectedExtras.map(extraSelection => (
                  <div
                    key={extraSelection.extra.id}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm text-gray-700">
                      {extraSelection.extra.name} (x{extraSelection.quantity})
                    </span>
                    <span className="font-medium text-gray-900">
                      $
                      {(
                        extraSelection.extra.price * extraSelection.quantity
                      ).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Pizzas */}
          {partyData.selectedPizzas.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">
                Selected Pizzas
              </h4>
              <div className="bg-gray-50 p-4 rounded space-y-2">
                {partyData.selectedPizzas.map(pizzaSelection => (
                  <div
                    key={pizzaSelection.pizza.id}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm text-gray-700">
                      {pizzaSelection.pizza.name} (x{pizzaSelection.quantity})
                    </span>
                    <span className="font-medium text-gray-900">
                      $
                      {(
                        pizzaSelection.pizza.price * pizzaSelection.quantity
                      ).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Details */}
          {partyData.additionalDetails &&
            (partyData.additionalDetails.guestName ||
              partyData.additionalDetails.notes) && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  Additional Details
                </h4>
                <div className="bg-gray-50 p-4 rounded space-y-3">
                  {partyData.additionalDetails.guestName && (
                    <div>
                      <p className="text-sm text-gray-600">Guest Name</p>
                      <p className="font-medium text-gray-900">
                        {partyData.additionalDetails.guestName}
                      </p>
                    </div>
                  )}
                  {partyData.additionalDetails.notes && (
                    <div>
                      <p className="text-sm text-gray-600">Notes</p>
                      <p className="text-gray-900">
                        {partyData.additionalDetails.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Customer Info */}
          {partyData.customerInfo && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">
                Customer Information
              </h4>
              <div className="bg-gray-50 p-4 rounded">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-gray-900">
                      {partyData.customerInfo.firstName}{' '}
                      {partyData.customerInfo.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">
                      {partyData.customerInfo.email}
                    </p>
                  </div>
                  {partyData.customerInfo.phoneNumber && (
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium text-gray-900">
                        {partyData.customerInfo.phoneNumber}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Total */}
          <div className="border-t border-gray-200 pt-4">
            <div className="bg-blue-50 p-4 rounded space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">
                  Subtotal:
                </span>
                <span className="text-lg font-semibold text-gray-900">
                  ${calculateTotal().toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">
                  Convenience Fee (3%):
                </span>
                <span className="text-lg font-semibold text-gray-900">
                  ${calculateConvenienceFee().toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center border-t border-blue-200 pt-2">
                <span className="text-lg font-semibold text-gray-900">
                  Total Amount:
                </span>
                <span className="text-2xl font-bold text-blue-600">
                  ${calculateFinalTotal().toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <SuccessAppDownloadBanner />

          {/* Important Information */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-900 mb-3">
              Important Information
            </h4>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <ul className="text-sm text-blue-800 space-y-1">
                <li>
                  • Please arrive 15 minutes before your scheduled party time
                </li>
                <li>
                  • Please make sure all skating guests complete their online
                  waiver at{' '}
                  <a
                    href="https://tickets.modestoonice.com/waivers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    https://tickets.modestoonice.com/waivers
                  </a>
                </li>
                <li>
                  • A confirmation email has been sent to your email address
                </li>
                <li>• Keep this confirmation for your records</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={handlePrint}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdPrint className="w-4 h-4 mr-2" />
            Print
          </button>
          <button
            onClick={handleSavePhoto}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <MdPhotoCamera className="w-4 h-4 mr-2" />
            Save Photo
          </button>
          <button
            onClick={handleDownloadPDF}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdDownload className="w-4 h-4 mr-2" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};
